import type { AgentUsage } from '../../types/shared';

export interface Organization {
  id: string;
  name: string;
  status: string;
  plan: string;
}

export const RECRAWL_INTERVAL_WEEKS = [1, 2, 4, 8, 16] as const;
export type RecrawlIntervalWeeks = (typeof RECRAWL_INTERVAL_WEEKS)[number];

// The switches of the settings tab, saved one at a time
export type KnowledgeBaseSettings = Pick<
  KnowledgeBaseApiResponse,
  | 'unanswered_questions_enabled'
  | 'company_info_in_prompt'
  | 'website_recrawl_enabled'
  | 'website_recrawl_interval_weeks'
>;

export interface KnowledgeBaseApiResponse {
  id: string;
  name: string;
  description: string;
  tags: string;
  status: 'active' | 'inactive';
  unanswered_questions_enabled: boolean;
  company_info_in_prompt: boolean;
  website_recrawl_enabled: boolean;
  website_recrawl_interval_weeks: RecrawlIntervalWeeks;
  organization: Organization;
  document_count: number;
  open_unanswered_questions_count: number;
  pending_website_pages_count: number;
  faq_entry_count: number;
  company_info_count: number;
  websites: { root_url: string; imported: boolean; import_failed: boolean }[];
  agents: KnowledgeBaseAgentApiResponse[];
  created: string;
  modified: string;
}

// The scheduling calendars are the active ones the agent books appointments into
export interface KnowledgeBaseAgentApiResponse extends AgentUsage {
  scheduling_profiles: SchedulingCalendarLink[];
}

export interface SchedulingCalendarLink {
  id: string;
  name: string;
}

export interface KnowledgeBaseAgent extends AgentUsage {
  schedulingCalendars: SchedulingCalendarLink[];
}

export interface DocumentApiResponse {
  id: string;
  title: string;
  knowledge_base: {
    id: string;
    name: string;
    description: string;
    tags: string;
    status: string;
    organization: Organization;
    document_count: number;
    created: string;
    modified: string;
  };
  file: string;
  file_size: string;
  file_size_verbose: string;
  tags: string;
  status: 'uploaded' | 'processing' | 'processed' | 'failed';
  content: string;
  metadata: string;
  organization: Organization;
  created: string;
  modified: string;
}

export interface Document {
  id: string;
  title: string;
  knowledgeBase: {
    id: string;
    name: string;
    description: string;
    tags?: string;
    status: string;
    organization: Organization;
    documentCount: number;
    created: string;
    modified: string;
  };
  file: string;
  fileSize: string;
  fileSizeVerbose: string;
  tags?: string;
  status: 'uploaded' | 'processing' | 'processed' | 'failed';
  content: string;
  metadata: string;
  organization: Organization;
  created: string;
  modified: string;
}

export interface KnowledgeBase {
  id: string;
  name: string;
  description: string;
  tags: string;
  status: 'active' | 'inactive' | 'processing' | 'error';
  unansweredQuestionsEnabled: boolean;
  companyInfoInPrompt: boolean;
  websiteRecrawlEnabled: boolean;
  websiteRecrawlIntervalWeeks: RecrawlIntervalWeeks;
  organization: Organization;
  documentCount: number;
  openUnansweredQuestionsCount: number;
  pendingWebsitePagesCount: number;
  faqEntryCount: number;
  companyInfoCount: number;
  websites: { rootUrl: string; imported: boolean; importFailed: boolean }[];
  agents: KnowledgeBaseAgent[];
  created: string;
  modified: string;
}

export interface KnowledgeBaseFormData {
  name: string;
  description: string;
}

export interface KnowledgeBaseFilters {
  search: string;
  status: string;
  knowledgeBase: string;
}

export interface UploadDocumentData {
  title: string;
  knowledgeBase: string;
  file: File;
  tags: string;
}

export interface CreateKnowledgeBaseData {
  name: string;
  description: string;
}

export type FaqEntryStatus = 'processing' | 'processed' | 'failed';
export type FaqEntrySource = 'manual' | 'unanswered_question';

export interface FaqEntry {
  id: string;
  knowledge_base: string;
  question: string;
  answer: string;
  source: FaqEntrySource;
  // Set for company info entries, empty for normal FAQ entries
  company_info_topic: CompanyInfoTopic | '';
  language: 'de' | 'en';
  status: FaqEntryStatus;
  // Whether the agent can find the entry; after a failed edit the previous version
  is_indexed: boolean;
  created: string;
  modified: string;
}

// Company info is saved as one FAQ entry per topic; the backend sets the question
export const COMPANY_INFO_TOPICS = [
  'address',
  'opening_hours',
  'contact',
  'directions',
  'payment_methods',
  'about',
  'team',
  'services',
] as const;

export type CompanyInfoTopic = (typeof COMPANY_INFO_TOPICS)[number];

// Only the given topics change; an empty answer deletes the topic's entry
export type CompanyInfoInput = Partial<Record<CompanyInfoTopic, string>>;

export interface FaqEntryInput {
  question: string;
  answer: string;
}

// Same limits as the backend: an entry is embedded as one chunk
export const FAQ_QUESTION_MAX_LENGTH = 500;
export const FAQ_ANSWER_MAX_LENGTH = 2000;

export type UnansweredQuestionStatus = 'open' | 'resolved_with_faq' | 'resolved' | 'dismissed';

export interface UnansweredQuestion {
  id: string;
  knowledge_base: string;
  // Phrased generally by the nightly detection, without personal data
  question: string;
  status: UnansweredQuestionStatus;
  occurrence_count: number;
  first_seen_at: string;
  last_seen_at: string;
  // Also set after the question came up again: shows which FAQ entry did not help
  resolved_faq_entry: { id: string; question: string; answer: string } | null;
  resolved_at: string | null;
  created: string;
}

export interface UnansweredQuestionOccurrence {
  id: string;
  conversation: string;
  // The call in the call list, which shows the transcript; null if none is found
  call: string | null;
  conversation_started_at: string | null;
  caller_number: string;
  original_question: string;
  context_excerpt: string;
}

export interface UnansweredQuestionDetail extends UnansweredQuestion {
  occurrences: UnansweredQuestionOccurrence[];
}

export type CrawlStatus = 'pending' | 'crawling' | 'succeeded' | 'failed';
export type PageSelection = 'pending' | 'included' | 'excluded';
export type PageIndexStatus = 'not_indexed' | 'processing' | 'indexed' | 'failed';

export interface WebsiteSource {
  id: string;
  knowledge_base: string;
  root_url: string;
  // Only the last crawl: after a failed re-crawl the previous pages stay active
  crawl_status: CrawlStatus;
  crawl_started_at: string | null;
  last_crawled_at: string | null;
  last_error: string;
  page_count: number;
  // New pages waiting for the customer's choice
  pending_page_count: number;
  included_page_count: number;
  created: string;
}

export interface WebsitePage {
  id: string;
  url: string;
  title: string;
  // The customer's choice; only included pages reach the agent
  selection: PageSelection;
  index_status: PageIndexStatus;
  // Preselection for pending pages (legal pages are not suggested)
  is_suggested: boolean;
  content_length: number;
  changed_at: string | null;
  last_seen_at: string;
  created: string;
}

export interface WebsitePageDetail extends WebsitePage {
  content: string;
}
